import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import { useSettingsStore } from '@/lib/settings-store';
import { useAdiaStore } from '@/lib/store';
import { cj1 } from '@/lib/content-lifecycle/contracts';
import { prayerContentSnapshot, prayerContentMatches, captureOutboundOwnerBinding, outboundOwnerStillCurrent } from '@/lib/outbound-prayer-authorization';
import { whoOf, isSelf } from '@/lib/prayer-utils';
import { getSupabase } from '@/lib/supabase';
import { stripCardLabelDisambig } from '@/lib/card-label';
import { isCrisisPrayer } from '@/lib/spine-crisis';
import { VERSE_SUGGEST_ENABLED } from '@/lib/feature-flags';
import {
  buildPrayerShareImage,
  downloadPrayerShareImage,
  formatShareTimestamp,
  type PrayerShareAsset,
} from '@/lib/export-prayer';
import { pickCardSize, type CardSize } from '@/lib/share-card-truncation';
import FullCardPreview from './FullCardPreview';
import ShareVersePicker, {
  type SelectedVerse,
  type ShareVersePickerHandle,
} from './ShareVersePicker';
import { useNavLayer } from '@/hooks/useNavLayer';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import styles from './LetThemKnowSheet.module.css';

interface Props {
  prayer: Prayer;
  onClose: () => void;
  /**
   * Re-reads the authoritative live Prayer immediately before an outbound
   * render/copy/download. Returns false when the prayer no longer exists in
   * the live store (deleted mid-flow), so nothing is rendered or sent.
   */
  authorizeOutbound: () => boolean;
  /** Coordinator z for the share-text / detail-share-text layer root (A1). */
  zIndex?: number;
}

// Truncation retired in Ship 2a.3; pickCardSize in
// src/lib/share-card-truncation.ts is the entire remaining surface,
// picking the verse size class (lg/md/sm) based on verse length.

// 'checking' covers the async session lookup; no card builds during it.
// 'authed' unlocks the verse step; 'guest' never sees it (taxonomy is
// authenticated-only).
type AuthPhase = 'checking' | 'authed' | 'guest';

// B2a preview-driven UX. Two-level state. `surface` chooses the
// rendered body; `question` chooses which question (or final actions)
// sit under the preview on the preview surface.
//
//   surface='preview' AND question='bibleVerse' → Q1 under live preview
//   surface='preview' AND question='cardLabel'  → Q2 under live preview
//   surface='preview' AND question=null         → final Copy
//   surface='versePicker'                       → ShareVersePicker body
//
//   The chrome around all surfaces is the same: X (cancel) top-left,
//   Prev chevron bottom-left (hidden on first step). The preview image
//   rebuilds live on each include/omit toggle via the existing
//   contentKey pattern. Yes/No buttons commit-and-advance — there is no
//   Next chevron (it would be vestigial vs Yes/No).
//
//   A prior Q3 (spine-catalog story prompt that surfaced curated
//   `display_prose` after a verse pick) was retired 2026-05-23. The
//   spine helper `fetchSpineProseForVerse` is dormant pending any
//   future pull-based discovery surface; see src/lib/scripture.ts for
//   the preserved query path and the rule #77 parallel.
type Surface = 'preview' | 'versePicker';
type Question = 'bibleVerse' | 'cardLabel';

function computeInitialQuestion(
  canAskVerse: boolean,
  canAskCardLabel: boolean,
): Question | null {
  if (canAskVerse) return 'bibleVerse';
  if (canAskCardLabel) return 'cardLabel';
  return null;
}

function nextAfter(
  currentQuestion: Question,
  canAskCardLabel: boolean,
): Question | null {
  if (currentQuestion === 'bibleVerse') {
    return canAskCardLabel ? 'cardLabel' : null;
  }
  return null; // 'cardLabel' is the last possible question
}

/**
 * Full-screen prayer-share-card flow with a preview-driven UX.
 *
 * Opened from PrayerDetail's leaf-sheet "Send a card", PrayPage's Garden
 * airplane and Remember share, and the Picker-enabled ChoiceSheet's
 * "Send as a text". `authorizeOutbound` performs a second live-store read
 * immediately before image render/copy/download, so a prayer deleted
 * mid-flow is never sent.
 *
 * B0 content model (rule #48 extended to the digital card): never raw
 * prayer text; never gardenSummary. The card carries the immutable
 * proof lines (Ship 2a.2: prefix + datetime stacked as two lines), an
 * optional curated verse (B2a.2), and an optional Grok-picked
 * cardLabel noun (B2a.3) — all opt-in via the questions below the
 * preview.
 *
 * Eligibility model (load-bearing — see Prev chevron logic below):
 * - canAskVerse = authPhase === 'authed' (verse picker is auth-only
 *   because the topic taxonomy is auth-only).
 * - canAskCardLabel = isEnglish && cardLabelLine truthy (purely
 *   client-side; works for guests).
 *
 * Default toggle state: both `includeVerse` and `includeCardLabel`
 * start false. The user opts IN explicitly. Initial preview = bare
 * proof-line-only card; subsequent toggles update the preview live.
 */
export default function LetThemKnowSheet({
  prayer,
  onClose,
  authorizeOutbound,
  zIndex,
}: Props) {
  const { t, i18n } = useTranslation();
  // App notice stack (v0.2.0 finishing): the in-flow spacer below the header
  // keeps the preview, the question and the Copy action below the fixed
  // notices (0 while no notice shows). Chrome only.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  // B2a render gate. The cardLabel list ships English-only at v1 (rule
  // #7 carve-out paired with grok-suggest-topics); es/pt parse prompts
  // return cardLabel: null structurally, but the gate also protects
  // against any drift. `slice(0, 2)` matches the existing convention at
  // src/lib/export-prayer.tsx:175 so locale tags like `en-US` / `en-GB`
  // collapse to `en` correctly.
  const isEnglish = (i18n.language ?? 'en').slice(0, 2) === 'en';
  const cardLabelRaw = prayer.parsed?.cardLabel ?? null;
  const cardLabelNoun =
    isEnglish && cardLabelRaw ? stripCardLabelDisambig(cardLabelRaw) : null;
  const cardLabelLine = cardLabelNoun
    ? `${t('share.cardLabelPrefix')} ${cardLabelNoun}`
    : undefined;
  const canAskCardLabel = Boolean(cardLabelLine);

  // Crisis mirror for the Find-Scripture eligibility below (memoized:
  // the regex sweep over up-to-20k chars should not run per render).
  const prayerIsCrisis = useMemo(() => isCrisisPrayer(prayer.rawText), [prayer.rawText]);

  // Synchronous init: no Supabase client → guest immediately, and we
  // already know enough to compute the initial question. With a client,
  // start in 'checking' and let the auth-resolve effect set the
  // question once we know whether canAskVerse is true.
  const hasClient = Boolean(getSupabase());
  const [authPhase, setAuthPhase] = useState<AuthPhase>(() =>
    hasClient ? 'checking' : 'guest',
  );
  const [surface, setSurface] = useState<Surface>('preview');
  const [question, setQuestion] = useState<Question | null>(() =>
    hasClient ? null : computeInitialQuestion(false, canAskCardLabel),
  );
  const [selectedVerse, setSelectedVerse] = useState<SelectedVerse | null>(null);
  // Default false on both — minimum card is proof-line-only; the
  // user opts in via the questions below the preview. State persists
  // across navigation so Prev → revise → forward preserves prior
  // answers.
  const [includeVerse, setIncludeVerse] = useState(false);
  const [includeCardLabel, setIncludeCardLabel] = useState(false);

  const prayerIdRef = useRef(prayer.id);

  // Ship 2a.1 (D7): tap-to-expand modal state. Parent owns the
  // expanded boolean so the state-gated Escape handler can route
  // Escape to either close the modal first OR close the sheet,
  // depending on which is on top. previewTriggerRef holds the
  // tappable preview-image button so we can restore focus to it when
  // the modal closes.
  const [previewExpanded, setPreviewExpanded] = useState(false);
  // full-card-preview nested layer (A1): registered here; the sheet mounts with
  // it closed (previewExpanded=false) -> StrictMode-safe. Browser-back now
  // closes it first (matching the existing state-gated Escape listener below).
  const zPreview = useNavLayer('full-card-preview', previewExpanded, () =>
    setPreviewExpanded(false),
  );
  const previewTriggerRef = useRef<HTMLButtonElement | null>(null);

  const canAskVerse = authPhase === 'authed';

  // Find-Scripture fork eligibility (share-scripture-found arc, plan
  // decision 7; the PrayerDetail spineEligible composition).
  const verseFindEligible =
    VERSE_SUGGEST_ENABLED &&
    isEnglish &&
    authPhase === 'authed' &&
    prayer.rawText.trim().length > 0 &&
    !prayerIsCrisis;
  const versePickerRef = useRef<ShareVersePickerHandle | null>(null);

  // The built asset is stored alongside the content key it was built for.
  // We only render/copy it when that key matches the current desired
  // content, so a changed prayer/verse/cardLabel/locale OR an
  // include/omit toggle can never leave a stale image visible or
  // copyable mid-rebuild.
  const [built, setBuilt] = useState<{ asset: PrayerShareAsset; key: string } | null>(null);
  const [buildError, setBuildError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [buildAttempt, setBuildAttempt] = useState(0);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const locale = i18n.language || 'en';

  // Ship 2a.2 — proof line split into two stacked rendered lines on the
  // card. Prefix carries the trailing colon ("I prayed for you on:"),
  // datetime renders below. Old single-line build (`prayedOnLabel`)
  // retired alongside the .prayedOn class.
  const mergeMap = useSettingsStore((state) => state.whoMergeMap);
  const resolvedWho = whoOf(prayer);
  const prefixKey =
    resolvedWho === 'General'
      ? 'share.cardMetaPrefixColonGeneral'
      : 'share.cardMetaPrefixColon';
  const prayedOnPrefix = t(prefixKey);
  const prayedOnDatetime = formatShareTimestamp(prayer.createdAt, locale);

  // Derived value order: RAW → EFFECTIVE → cardSize → contentKey.
  // cardSize is computed from EFFECTIVE verse content so opt-out never
  // leaves a stale "included-content" size class.

  // Raw picker output (no truncation; verse renders in full).
  const verseText = selectedVerse?.text;
  const verseReference = selectedVerse?.reference;

  // Effective values reflect include/omit choices. Build call AND
  // contentKey BOTH key off these (NOT raw values) so opt-out never
  // lets a stale "included-content" PNG be copied.
  const effectiveVerseText = includeVerse ? verseText : undefined;
  const effectiveVerseReference = includeVerse ? verseReference : undefined;
  const effectiveCardLabelLine = includeCardLabel ? cardLabelLine : undefined;

  // Size class picked from EFFECTIVE verse length. Falls back to 'lg'
  // when no verse picked — the size class is unused in that state
  // since the verse slot doesn't render, but it gives a stable value
  // for the contentKey when verses come and go through Prev/revise.
  const cardSize: CardSize = pickCardSize(effectiveVerseText?.length ?? 0);

  const contentSnapshot = prayerContentSnapshot(prayer);
  const ownerBinding = captureOutboundOwnerBinding();
  const descriptor = {
    snapshot: contentSnapshot,
    owner: ownerBinding,
    resolvedWho,
    locale,
    content: { prayedOnPrefix, prayedOnDatetime,
      verseText: effectiveVerseText ?? null, verseReference: effectiveVerseReference ?? null,
      cardLabelLine: effectiveCardLabelLine ?? null, cardSize },
    buildAttempt,
  };
  const contentKey = cj1(descriptor);
  const currentKey = useRef(contentKey);
  const buildGeneration = useRef(0);
  useLayoutEffect(() => { currentKey.current = contentKey; }, [contentKey]);
  // A merge can change twice before React paints. Invalidate held assets on
  // each source transition, even when the eventual resolved name matches.
  useEffect(() => useSettingsStore.subscribe((next, previous) => {
    if (next.whoMergeMap !== previous.whoMergeMap) {
      buildGeneration.current += 1;
      setBuildAttempt((attempt) => attempt + 1);
    }
  }), []);
  const inputStillCurrent = () => {
    const live = useAdiaStore.getState().prayers.find((row) => row.id === prayer.id);
    return currentKey.current === contentKey &&
      i18n.language === locale && useSettingsStore.getState().whoMergeMap === mergeMap &&
      (ownerBinding === null ? captureOutboundOwnerBinding() === null : outboundOwnerStillCurrent(ownerBinding)) &&
      live !== undefined && prayerContentMatches(contentSnapshot, live) && whoOf(live) === resolvedWho;
  };


  // Resolve the session once. After resolve, compute the initial
  // question from canAskVerse + canAskCardLabel — guest with en +
  // cardLabel lands on Q2 directly (verse step is auth-only and is
  // structurally skipped). The hasClient branch was synchronously
  // resolved in the state initializers.
  useEffect(() => {
    const sb = getSupabase();
    if (!sb) return;
    let cancelled = false;
    sb.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        const nextAuth: AuthPhase = data.session ? 'authed' : 'guest';
        setAuthPhase(nextAuth);
        const canVerseNow = nextAuth === 'authed';
        setQuestion(computeInitialQuestion(canVerseNow, canAskCardLabel));
      })
      .catch(() => {
        if (cancelled) return;
        setAuthPhase('guest');
        setQuestion(computeInitialQuestion(false, canAskCardLabel));
      });
    return () => {
      cancelled = true;
    };
  }, [prayer, canAskCardLabel]);

  // Escape closes the topmost surface. With the Ship 2a.1 FullCardPreview
  // modal in play, Escape MUST close the modal first when it's open;
  // otherwise it falls through to close the sheet (existing behavior).
  // Single window listener owned here so there's no race between parent
  // and child handlers (codex pass-6 BLOCKER 2). FullCardPreview does
  // NOT register its own Escape listener.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (previewExpanded) {
        setPreviewExpanded(false);
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, previewExpanded]);

  // Restore focus to the preview trigger when the modal closes (the
  // open path is owned by FullCardPreview's own useEffect on isOpen).
  // Track the previous value so we only act on the true → false edge.
  const prevExpandedRef = useRef(false);
  useEffect(() => {
    if (prevExpandedRef.current && !previewExpanded) {
      previewTriggerRef.current?.focus();
    }
    prevExpandedRef.current = previewExpanded;
  }, [previewExpanded]);

  // Prayer-swap cleanup: fires only on a real swap, not on first
  // mount. Today's mount path always creates a fresh sheet per share,
  // but if a future caller keeps the sheet mounted while swapping
  // prayer, the verse-side pick state must reset cleanly to prevent
  // the prior prayer's verse choices from leaking onto the new prayer.
  // Resets verse-side state only (selectedVerse + includeVerse);
  // includeCardLabel is preserved since the new prayer may carry a
  // different cardLabel — fresh-mount-per-share makes this defensive
  // parity, not a fresh-choices rewrite. The ref update lives ABOVE
  // the authPhase guard so a swap during 'checking' doesn't leave the
  // ref stuck on the old prayer — when auth later resolves, the ref
  // already matches and the swap reset doesn't re-fire.
  useEffect(() => {
    if (prayerIdRef.current === prayer.id) return;
    prayerIdRef.current = prayer.id;
    if (authPhase === 'checking') return;
    // Documented deviation per CLAUDE.md rule #75: setState in effect
    // is intentional here — the effect IS the prayer-swap reset
    // trigger, equivalent to a URL-param-driven re-fetch. Resetting
    // verse-side state derivatively from prayer.id is the whole point.
    // (The set-state-in-effect rule stopped flagging these calls after
    // the share-scripture-found edits elsewhere in this component; the
    // deviation is still deliberate and this comment is its record.)
    setSelectedVerse(null);
    setIncludeVerse(false);
    setQuestion(computeInitialQuestion(canAskVerse, canAskCardLabel));
  }, [prayer.id, authPhase, canAskVerse, canAskCardLabel]);

  // Build the card PNG whenever the preview surface is rendered with
  // resolved auth. State resets live in the handlers, not here, per
  // react-hooks/set-state-in-effect — every Yes/No/Pick/Prev handler
  // calls `resetTransient()` to clear buildError + copied + (if error
  // was set) bump buildAttempt to guarantee a fresh build on return
  // from any navigation.
  useEffect(() => {
    if (surface !== 'preview' || authPhase === 'checking') return;
    // Rendering a shareable image is itself an outbound preparation boundary.
    // The parent reads useAdiaStore.getState() here, rather than trusting this
    // render's Prayer prop, so a same-tick deletion fails closed.
    if (!authorizeOutbound()) return;
    let cancelled = false;
    const generation = buildGeneration.current;
    buildPrayerShareImage(prayer, {
      prayedOnPrefix,
      prayedOnDatetime,
      verseText: effectiveVerseText,
      verseReference: effectiveVerseReference,
      cardLabelLine: effectiveCardLabelLine,
      cardSize,
    }, resolvedWho)
      .then((result) => {
        if (!cancelled && generation === buildGeneration.current && inputStillCurrent()) {
          setBuilt({ asset: result, key: contentKey });
        }
      })
      .catch(() => {
        if (!cancelled && generation === buildGeneration.current && inputStillCurrent()) setBuildError(true);
      });
    return () => {
      cancelled = true;
    };
    // `prayer.id` + `prayer.parsed` stand in for the prayer object: the
    // builder consumes only whoOf(prayer) (parsed.who), and an
    // object-identity dep would rebuild the identical card when a
    // match-result persist (patchPrayer) swaps the store object without
    // touching parsed (share-scripture-found arc execution check).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    surface,
    authPhase,
    prayer.id,
    prayer.updatedAt,
    prayer.parsed,
    prayedOnPrefix,
    prayedOnDatetime,
    effectiveVerseText,
    effectiveVerseReference,
    effectiveCardLabelLine,
    cardSize,
    contentKey,
    mergeMap,
    authorizeOutbound,
  ]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const ready = built !== null && built.key === contentKey;
  const asset = ready ? built.asset : null;
  const showCopied = copied && ready;

  // Transient-state reset shared by every navigation handler. If
  // buildError was set, also bump buildAttempt so the next navigation
  // guarantees a fresh build attempt — otherwise a user returning to
  // a state whose contentKey hasn't changed could be stuck on the
  // pulse with no error message and no rebuild trigger (codex pass-2
  // LOW 3 corner case).
  const resetTransient = () => {
    setCopied(false);
    if (buildError) {
      setBuildError(false);
      setBuildAttempt((n) => n + 1);
    }
  };

  const handleCopy = async () => {
    if (!asset || !inputStillCurrent()) return;
    const generation = buildGeneration.current;
    if (!authorizeOutbound()) return;
    try {
      const item = new ClipboardItem({ 'image/png': Promise.resolve(asset.blob) });
      await navigator.clipboard.write([item]);
      if (generation !== buildGeneration.current || !inputStillCurrent()) return;
      setCopied(true);
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 4000);
    } catch {
      // Clipboard failure falls back to a download, which is a distinct final
      // outbound action and therefore gets its own just-in-time re-read.
      if (generation !== buildGeneration.current || !inputStillCurrent() || !authorizeOutbound()) return;
      downloadPrayerShareImage(asset);
    }
  };

  const handleRetry = () => {
    setBuildError(false);
    setCopied(false);
    setBuildAttempt((n) => n + 1);
  };

  // Q1 "Add a Bible verse" → route to the picker. includeVerse stays
  // false until pick commits (so a Prev from the picker cleanly undoes
  // the Yes).
  const handleQ1AddVerse = () => {
    resetTransient();
    setSurface('versePicker');
  };

  // Q1 "Skip" → leave verse fields empty, advance to next applicable
  // question (cardLabel if eligible, else done). Clears any prior
  // pick state in case the user backed into Q1 from somewhere further
  // down the flow.
  const handleQ1Skip = () => {
    resetTransient();
    setSelectedVerse(null);
    setIncludeVerse(false);
    setQuestion(nextAfter('bibleVerse', canAskCardLabel));
  };

  // Verse picked. Commit verse + advance to next eligible question
  // (cardLabel if eligible, else done).
  const handlePickVerse = (verse: SelectedVerse) => {
    resetTransient();
    setSelectedVerse(verse);
    setIncludeVerse(true);
    setSurface('preview');
    setQuestion(nextAfter('bibleVerse', canAskCardLabel));
  };

  const handleQ2Include = () => {
    resetTransient();
    setIncludeCardLabel(true);
    setQuestion(null);
  };

  const handleQ2Skip = () => {
    resetTransient();
    setIncludeCardLabel(false);
    setQuestion(null);
  };

  // Prev chevron: steps backward through whichever sequence actually
  // ran. Eligibility is gated by `canAskVerse` / `canAskCardLabel` so
  // a guest never reaches the auth-only verse step via Prev, and a
  // non-en user never reaches the cardLabel step via Prev (the gates
  // also drive whether Prev is rendered at all — see `showPrev`).
  const handlePrev = () => {
    resetTransient();
    if (surface === 'versePicker') {
      // One internal picker step first (found/browse -> fork); only at
      // the picker's root does Prev exit the verse step (the goBack
      // imperative handle owns the internal levels, plan decision 8).
      if (versePickerRef.current?.goBack()) return;
      // Undoing the Q1 Yes that brought us here. Clear the verse-side
      // pick state so the user returns to Q1 with a fresh slate.
      setSelectedVerse(null);
      setIncludeVerse(false);
      setSurface('preview');
      setQuestion('bibleVerse');
      return;
    }
    // surface === 'preview'
    if (question === 'bibleVerse') {
      // First step; Prev is hidden in this state, defensive no-op.
      return;
    }
    if (question === 'cardLabel') {
      if (!canAskVerse) {
        // Q1 was structurally skipped; cardLabel WAS the initial
        // state. Prev is hidden, defensive no-op.
        return;
      }
      if (selectedVerse) {
        // REVISE path: return to picker, preserving the picked verse.
        setSurface('versePicker');
        return;
      }
      setQuestion('bibleVerse');
      return;
    }
    // question === null (done)
    if (canAskCardLabel) {
      setQuestion('cardLabel');
      return;
    }
    if (canAskVerse && selectedVerse) {
      setSurface('versePicker');
      return;
    }
    if (canAskVerse) {
      setQuestion('bibleVerse');
      return;
    }
    // Defensive no-op: initial state was already done.
  };

  // `showPrev` controls whether the bottom Prev row renders. Hidden
  // on the first step of whatever sequence actually ran so the user
  // never sees a disabled chevron leading nowhere.
  const showPrev = (() => {
    if (authPhase === 'checking') return false;
    if (surface === 'versePicker') return true;
    if (question === 'bibleVerse') return false;
    if (question === 'cardLabel') return canAskVerse;
    // question === null (done)
    return canAskCardLabel || canAskVerse;
  })();

  const rawWho = whoOf(prayer);
  const isGeneral = rawWho === 'General';
  const recipientName = isSelf(rawWho) ? t('people.you') : rawWho;
  const sheetTitle = isGeneral
    ? t('share.sheetTitleGeneral')
    : t('share.sheetTitle', { name: recipientName });

  const renderPreviewSlot = () => {
    if (buildError) {
      // Inline error inside the preview slot only — leaves the header,
      // question prompt, action buttons, and Prev chevron all intact
      // and interactive so the user can revise answers or try again
      // without losing flow context.
      return (
        <div className={styles.previewError}>
          <p className={styles.previewErrorMessage}>{t('share.failed')}</p>
          <button
            type="button"
            className={styles.previewErrorRetry}
            onClick={handleRetry}
          >
            {t('share.tryAgain')}
          </button>
        </div>
      );
    }
    if (asset) {
      // Ship 2a.1 (D7): wrap ONLY the asset img in a button so users
      // can tap to expand the card to a readable size. The error and
      // pulse branches stay plain divs — wrapping the whole preview
      // slot in a button would create nested-button issues with the
      // error state's retry button (codex pass-6 BLOCKER 3).
      return (
        <button
          ref={previewTriggerRef}
          type="button"
          className={styles.previewExpandBtn}
          onClick={() => setPreviewExpanded(true)}
          aria-label={t('share.viewFullCard')}
        >
          <img
            src={asset.dataUrl}
            alt={t('share.imageAlt')}
            className={styles.previewImg}
            width={asset.width}
            height={asset.height}
          />
          <span className={styles.previewExpandBadge} aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
              <line x1="11" y1="8" x2="11" y2="14" />
              <line x1="8" y1="11" x2="14" y2="11" />
            </svg>
          </span>
        </button>
      );
    }
    return <div className={styles.previewPulse} aria-hidden="true" />;
  };

  const renderActionsBlock = () => {
    if (question === 'bibleVerse') {
      return (
        <div className={styles.actions}>
          <p className={styles.questionPrompt}>{t('share.bibleVersePrompt')}</p>
          <button type="button" className={styles.copyButton} onClick={handleQ1AddVerse}>
            {t('share.bibleVerseIncludeBtn')}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={handleQ1Skip}>
            <span>{t('share.bibleVerseSkipBtn')}</span>
          </button>
        </div>
      );
    }
    if (question === 'cardLabel') {
      return (
        <div className={styles.actions}>
          <p className={styles.questionPrompt}>{t('share.cardLabelPrompt')}</p>
          <button type="button" className={styles.copyButton} onClick={handleQ2Include}>
            {t('share.cardLabelIncludeBtn', { noun: cardLabelNoun ?? '' })}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={handleQ2Skip}>
            <span>{t('share.cardLabelSkipBtn')}</span>
          </button>
        </div>
      );
    }
    // question === null (done)
    return (
      <div className={styles.actions}>
        <p
          className={`${styles.hint} ${showCopied ? styles.hintCopied : ''}`}
          aria-live="polite"
        >
          {asset
            ? showCopied
              ? t('share.copiedHint')
              : t('share.copyHint')
            : t('share.building')}
        </p>
        <button
          type="button"
          className={`${styles.copyButton} ${showCopied ? styles.copyButtonDone : ''}`}
          onClick={handleCopy}
          disabled={!asset}
        >
          {showCopied ? t('share.copyButtonDone') : t('share.copyButton')}
        </button>
      </div>
    );
  };

  return (
    <div className={styles.root} style={{ zIndex }} role="dialog" aria-modal="true" aria-label={sheetTitle}>
      <header className={styles.header}>
        <button
          type="button"
          className={styles.headerBtn}
          onClick={onClose}
          aria-label={t('nav.cancel')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span className={styles.headerBtnLabel}>{t('nav.cancel')}</span>
        </button>
        <h1 className={styles.headerTitle}>{sheetTitle}</h1>
      </header>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      {authPhase === 'checking' ? (
        <div className={styles.body}>
          <div className={styles.preview}>
            <div className={styles.previewPulse} aria-hidden="true" />
          </div>
        </div>
      ) : surface === 'versePicker' ? (
        <div className={`${styles.body} ${styles.bodyScroll}`}>
          <ShareVersePicker
            ref={versePickerRef}
            onPick={handlePickVerse}
            findPrayer={verseFindEligible ? prayer : undefined}
          />
        </div>
      ) : (
        <div className={styles.body}>
          {/* Ship 2a.3 — plumb actual PNG aspect via two CSS custom
              properties so the .preview slot frame matches the
              captured card no matter how tall it grew.
              `--card-aspect` drives `aspect-ratio`;
              `--card-width-per-height` is the height-derived width
              term inside the slot's `width: min(...)` formula (the
              third `min()` argument). Both fall back to 9/20=0.45
              while no asset is ready, matching the pre-Ship-2a.3
              fixed shape. */}
          <div
            className={styles.preview}
            style={
              asset
                ? ({
                    '--card-aspect': `${asset.width} / ${asset.height}`,
                    '--card-width-per-height': asset.width / asset.height,
                  } as CSSProperties)
                : undefined
            }
          >
            {renderPreviewSlot()}
          </div>
          {renderActionsBlock()}
        </div>
      )}

      {showPrev && (
        <div className={styles.prevRow}>
          <button
            type="button"
            className={styles.prevBtn}
            onClick={handlePrev}
            aria-label={t('nav.back')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        </div>
      )}

      {/* Ship 2a.1 (D7) — tap-to-expand modal portals to body. Only
          opens when there's an asset to show; the preview-expand
          button only renders in the asset-ready branch of
          renderPreviewSlot. */}
      <FullCardPreview
        isOpen={previewExpanded}
        dataUrl={asset?.dataUrl ?? null}
        cardWidth={asset?.width ?? null}
        cardHeight={asset?.height ?? null}
        ariaLabel={sheetTitle}
        onClose={() => setPreviewExpanded(false)}
        zIndex={zPreview}
      />
    </div>
  );
}
